import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  ChevronLeft, ChevronRight, CalendarDays, Droplets, Zap,
  TrendingUp, BookOpen, Plus, CheckCircle2, Clock, X, Trash2
} from 'lucide-react';
import DatePicker from '../components/DatePicker';

const API = process.env.REACT_APP_BACKEND_URL;

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const MONTHS = ['Januar','Februar','März','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember'];

const CARE_COLORS = {
  watering:   { bg: 'rgba(59,130,246,0.2)',  text: '#60A5FA', border: 'rgba(59,130,246,0.4)' },
  fertilizing:{ bg: 'rgba(245,158,11,0.2)',  text: '#FBBF24', border: 'rgba(245,158,11,0.4)' },
  training:   { bg: 'rgba(16,185,129,0.2)',  text: '#34D399', border: 'rgba(16,185,129,0.4)' },
  topping:    { bg: 'rgba(139,92,246,0.2)',  text: '#A78BFA', border: 'rgba(139,92,246,0.4)' },
  defoliation:{ bg: 'rgba(239,68,68,0.2)',   text: '#F87171', border: 'rgba(239,68,68,0.4)'  },
  repotting:  { bg: 'rgba(217,119,6,0.2)',   text: '#FCD34D', border: 'rgba(217,119,6,0.4)'  },
  other:      { bg: 'rgba(107,114,128,0.2)', text: '#9CA3AF', border: 'rgba(107,114,128,0.4)'},
  diary:      { bg: 'rgba(99,102,241,0.2)',  text: '#818CF8', border: 'rgba(99,102,241,0.4)' },
  planned:    { bg: 'rgba(251,191,36,0.12)', text: '#FCD34D', border: 'rgba(251,191,36,0.35)' },
};

const CARE_OPTIONS = [
  { value: 'watering',   label: 'Gießen' },
  { value: 'fertilizing',label: 'Düngen' },
  { value: 'training',   label: 'Trainieren' },
  { value: 'topping',    label: 'Topping' },
  { value: 'defoliation',label: 'Entblätterung' },
  { value: 'repotting',  label: 'Umtopfen' },
  { value: 'other',      label: 'Sonstiges' },
];
const CARE_LABELS = Object.fromEntries(CARE_OPTIONS.map(o => [o.value, o.label]));

const CARE_ICONS = { watering: Droplets, fertilizing: Zap, training: TrendingUp, diary: BookOpen };

function getDaysInMonth(year, month) { return new Date(year, month + 1, 0).getDate(); }
function getFirstWeekday(year, month) { const d = new Date(year, month, 1).getDay(); return d === 0 ? 6 : d - 1; }
function formatDateKey(year, month, day) {
  return `${year}-${String(month + 1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
}

export default function CalendarPage() {
  const now = new Date();
  const [year, setYear]   = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [calData, setCalData] = useState({ care_logs: {}, planned_logs: {}, diary: {} });
  const [loading, setLoading]   = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [careTypes, setCareTypes]     = useState([]);
  const [plants, setPlants]           = useState([]);

  // Plan modal state
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [planDate, setPlanDate]   = useState('');
  const [planForm, setPlanForm]   = useState({ plant_ids: [], care_type: 'watering', amount: '', notes: '' });
  const [savingPlan, setSavingPlan] = useState(false);
  const [planError, setPlanError]   = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [calRes, ctRes, plRes] = await Promise.all([
        axios.get(`${API}/api/calendar?year=${year}&month=${month + 1}`),
        axios.get(`${API}/api/care-types`),
        axios.get(`${API}/api/plants`),
      ]);
      setCalData(calRes.data && typeof calRes.data === 'object' ? calRes.data : { care_logs: {}, planned_logs: {}, diary: {} });
      setCareTypes(Array.isArray(ctRes.data) ? ctRes.data : []);
      setPlants(Array.isArray(plRes.data) ? plRes.data : []);
    } catch { setCareTypes([]); setPlants([]); }
    setLoading(false);
  }, [year, month]);

  useEffect(() => { loadData(); }, [loadData]);

  const prevMonth = () => { if (month === 0) { setYear(y => y-1); setMonth(11); } else setMonth(m=>m-1); setSelectedDay(null); };
  const nextMonth = () => { if (month === 11) { setYear(y => y+1); setMonth(0); } else setMonth(m=>m+1); setSelectedDay(null); };

  const daysInMonth  = getDaysInMonth(year, month);
  const firstWeekday = getFirstWeekday(year, month);
  const today = new Date();

  const getCareColor = (type) => CARE_COLORS[type] || CARE_COLORS.other;
  const getCareLabel = (type) => {
    const ct = careTypes.find(c => c.value === type);
    return ct?.label || CARE_LABELS[type] || type;
  };

  const selectedKey      = selectedDay ? formatDateKey(year, month, selectedDay) : null;
  const selectedLogs     = selectedKey ? (calData.care_logs[selectedKey] || []) : [];
  const selectedPlanned  = selectedKey ? (calData.planned_logs[selectedKey] || []) : [];
  const selectedDiary    = selectedKey ? (calData.diary[selectedKey] || []) : [];

  // Open plan modal for a specific day
  const openPlanModal = (day) => {
    const dateStr = formatDateKey(year, month, day);
    setPlanDate(dateStr);
    setPlanForm({ plant_ids: [], care_type: 'watering', amount: '', notes: '' });
    setPlanError('');
    setShowPlanModal(true);
  };

  const togglePlant = (id) => setPlanForm(f => ({
    ...f, plant_ids: f.plant_ids.includes(id) ? f.plant_ids.filter(p => p !== id) : [...f.plant_ids, id]
  }));

  const handleSavePlan = async (e) => {
    e.preventDefault();
    if (planForm.plant_ids.length === 0) { setPlanError('Mindestens eine Pflanze auswählen'); return; }
    setSavingPlan(true);
    setPlanError('');
    try {
      await axios.post(`${API}/api/care-logs`, {
        ...planForm,
        performed_at: `${planDate}T09:00:00`,
        is_planned: true,
      });
      await loadData();
      setShowPlanModal(false);
    } catch (err) { setPlanError(err.response?.data?.detail || 'Fehler'); }
    setSavingPlan(false);
  };

  const handleComplete = async (logId) => {
    try {
      await axios.put(`${API}/api/care-logs/${logId}/complete`);
      await loadData();
    } catch {}
  };

  const handleDeleteLog = async (logId) => {
    if (!window.confirm('Eintrag löschen?')) return;
    try {
      await axios.delete(`${API}/api/care-logs/${logId}`);
      await loadData();
    } catch {}
  };

  return (
    <div className="p-4 md:p-6 space-y-4 animate-fade-in" data-testid="calendar-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-emerald-50">Grow-Kalender</h1>
        <p className="text-emerald-100/50 text-sm mt-0.5">Pflegevorgänge & Planung</p>
      </div>

      {/* Calendar Card */}
      <div className="card-grow p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <button onClick={prevMonth} className="p-2 rounded-lg text-emerald-100/60 hover:text-emerald-100 hover:bg-[#111C15] transition-colors" data-testid="prev-month-btn">
            <ChevronLeft size={20} />
          </button>
          <h2 className="font-heading font-semibold text-emerald-50 text-lg">{MONTHS[month]} {year}</h2>
          <button onClick={nextMonth} className="p-2 rounded-lg text-emerald-100/60 hover:text-emerald-100 hover:bg-[#111C15] transition-colors" data-testid="next-month-btn">
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Weekday headers */}
        <div className="grid grid-cols-7 mb-1">
          {WEEKDAYS.map(d => (
            <div key={d} className="text-center text-xs text-emerald-100/30 py-1 font-medium">{d}</div>
          ))}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <div className="grid grid-cols-7 gap-0.5" data-testid="calendar-grid">
            {Array.from({ length: firstWeekday }, (_, i) => <div key={`e-${i}`} className="aspect-square" />)}
            {Array.from({ length: daysInMonth }, (_, i) => {
              const day       = i + 1;
              const dateKey   = formatDateKey(year, month, day);
              const logs      = calData.care_logs[dateKey] || [];
              const planned   = calData.planned_logs[dateKey] || [];
              const diary     = calData.diary[dateKey] || [];
              const isToday   = today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;
              const isSelected = selectedDay === day;
              const isFuture  = new Date(dateKey) > today;
              const hasEvents = logs.length > 0 || diary.length > 0 || planned.length > 0;

              return (
                <button key={day}
                  onClick={() => setSelectedDay(isSelected ? null : day)}
                  className={`aspect-square rounded-lg flex flex-col items-center p-1 transition-all relative ${
                    isSelected
                      ? 'bg-emerald-500/30 border border-emerald-500/60 text-emerald-300'
                      : isToday
                      ? 'border border-emerald-500/40 text-emerald-400'
                      : hasEvents ? 'text-emerald-100 hover:bg-[#111C15]'
                      : 'text-emerald-100/40 hover:bg-[#111C15]'
                  }`}
                  style={{ background: isSelected ? undefined : isToday ? 'rgba(16,185,129,0.06)' : undefined }}
                  data-testid={`day-${dateKey}`}>
                  <span className={`text-xs font-medium ${isToday ? 'font-bold' : ''}`}>{day}</span>
                  {hasEvents && (
                    <div className="flex gap-0.5 mt-0.5 flex-wrap justify-center">
                      {logs.slice(0, 2).map((l, idx) => (
                        <span key={idx} className="w-1.5 h-1.5 rounded-full" style={{ background: getCareColor(l.care_type).text }} />
                      ))}
                      {planned.length > 0 && (
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#FCD34D', opacity: 0.7 }} />
                      )}
                      {diary.length > 0 && (
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: CARE_COLORS.diary.text }} />
                      )}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Legend */}
        <div className="flex flex-wrap gap-3 mt-4 pt-3 border-t" style={{ borderColor: '#1B2A21' }}>
          {[
            { type: 'watering',   label: 'Gießen' },
            { type: 'fertilizing',label: 'Düngen' },
            { type: 'training',   label: 'Trainieren' },
            { type: 'diary',      label: 'Tagebuch' },
          ].map(({ type, label }) => (
            <div key={type} className="flex items-center gap-1.5 text-xs text-emerald-100/50">
              <span className="w-2 h-2 rounded-full" style={{ background: CARE_COLORS[type].text }} />
              {label}
            </div>
          ))}
          <div className="flex items-center gap-1.5 text-xs text-emerald-100/50">
            <span className="w-2 h-2 rounded-full opacity-70" style={{ background: '#FCD34D' }} />
            Geplant
          </div>
        </div>
      </div>

      {/* Selected Day Detail */}
      {selectedDay && (
        <div className="card-grow p-4 animate-slide-up" data-testid="day-detail">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-semibold text-emerald-100 flex items-center gap-2">
              <CalendarDays size={16} className="text-emerald-400" />
              {selectedDay}. {MONTHS[month]} {year}
            </h3>
            <button onClick={() => openPlanModal(selectedDay)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-emerald-400 hover:bg-emerald-500/10 transition-colors"
              style={{ border: '1px solid rgba(16,185,129,0.3)' }}
              data-testid="plan-event-btn">
              <Plus size={14} /> Planen
            </button>
          </div>

          {selectedLogs.length === 0 && selectedPlanned.length === 0 && selectedDiary.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-sm text-emerald-100/30">Keine Einträge</p>
              <button onClick={() => openPlanModal(selectedDay)}
                className="text-sm text-emerald-400 hover:text-emerald-300 mt-1 transition-colors">
                Pflegevorgang planen
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {/* Completed logs */}
              {selectedLogs.map(log => {
                const color = getCareColor(log.care_type);
                const Icon = CARE_ICONS[log.care_type] || CalendarDays;
                return (
                  <div key={log.id} className="flex items-start gap-3 p-2.5 rounded-lg"
                    style={{ background: color.bg, border: `1px solid ${color.border}` }}>
                    <Icon size={14} style={{ color: color.text }} className="mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium" style={{ color: color.text }}>
                        {getCareLabel(log.care_type)}
                      </span>
                      {log.amount && <span className="text-xs ml-2 opacity-70" style={{ color: color.text }}>{log.amount}</span>}
                      <p className="text-xs text-emerald-100/40 mt-0.5">{log.plant_ids?.length} Pflanze(n)</p>
                      {log.notes && <p className="text-xs text-emerald-100/40 truncate">{log.notes}</p>}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <span className="text-xs text-emerald-100/30">
                        {log.performed_at ? new Date(log.performed_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : ''}
                      </span>
                      <button onClick={() => handleDeleteLog(log.id)} className="p-1 text-emerald-100/30 hover:text-red-400 transition-colors" data-testid={`delete-log-${log.id}`}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                );
              })}

              {/* Planned logs */}
              {selectedPlanned.map(log => {
                const color = getCareColor(log.care_type);
                const Icon  = CARE_ICONS[log.care_type] || CalendarDays;
                return (
                  <div key={log.id} className="flex items-start gap-3 p-2.5 rounded-lg"
                    style={{ background: CARE_COLORS.planned.bg, border: `1px dashed ${CARE_COLORS.planned.border}` }}>
                    <div className="flex items-center gap-1.5 flex-shrink-0 mt-0.5">
                      <Clock size={12} style={{ color: '#FCD34D' }} />
                      <Icon size={14} style={{ color: color.text }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium" style={{ color: color.text }}>{getCareLabel(log.care_type)}</span>
                        <span className="badge badge-yellow text-xs">Geplant</span>
                      </div>
                      {log.amount && <span className="text-xs opacity-70" style={{ color: color.text }}>{log.amount}</span>}
                      <p className="text-xs text-emerald-100/40 mt-0.5">{log.plant_ids?.length} Pflanze(n)</p>
                      {log.notes && <p className="text-xs text-emerald-100/40 truncate">{log.notes}</p>}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={() => handleComplete(log.id)}
                        className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs text-emerald-400 hover:bg-emerald-500/20 transition-colors"
                        style={{ border: '1px solid rgba(16,185,129,0.3)' }}
                        data-testid={`complete-log-${log.id}`}>
                        <CheckCircle2 size={12} /> Erledigt
                      </button>
                      <button onClick={() => handleDeleteLog(log.id)} className="p-1 text-emerald-100/30 hover:text-red-400 transition-colors">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                );
              })}

              {/* Diary entries */}
              {selectedDiary.map(entry => (
                <div key={entry.id} className="flex items-center gap-3 p-2.5 rounded-lg"
                  style={{ background: CARE_COLORS.diary.bg, border: `1px solid ${CARE_COLORS.diary.border}` }}>
                  <BookOpen size={14} style={{ color: CARE_COLORS.diary.text }} className="flex-shrink-0" />
                  <span className="text-sm" style={{ color: CARE_COLORS.diary.text }}>{entry.title}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Plan Modal */}
      {showPlanModal && (
        <div className="modal-backdrop" data-testid="plan-modal">
          <div className="modal-content p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-heading font-semibold text-emerald-50">Pflege planen</h2>
                <p className="text-sm text-emerald-100/50 mt-0.5">
                  {planDate ? new Date(planDate).toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long' }) : ''}
                </p>
              </div>
              <button onClick={() => setShowPlanModal(false)} className="text-emerald-100/40 hover:text-emerald-100"><X size={20} /></button>
            </div>
            <form onSubmit={handleSavePlan} className="space-y-4">
              {/* Date */}
              <div>
                <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Datum</label>
                <DatePicker
                  value={planDate}
                  onChange={v => setPlanDate(v)}
                  placeholder="Datum wählen"
                  data-testid="plan-date-input"
                />
              </div>

              {/* Care type */}
              <div>
                <label className="block text-xs text-emerald-100/60 mb-2 uppercase tracking-wider">Pflegetyp</label>
                <div className="grid grid-cols-3 gap-2">
                  {CARE_OPTIONS.slice(0, 6).map(opt => (
                    <button key={opt.value} type="button"
                      onClick={() => setPlanForm(f => ({ ...f, care_type: opt.value }))}
                      className={`py-2 px-2 rounded-lg text-xs font-medium transition-colors ${planForm.care_type === opt.value ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40' : 'text-emerald-100/50 hover:bg-[#050A07]'}`}
                      style={{ border: planForm.care_type === opt.value ? undefined : '1px solid #1B2A21' }}
                      data-testid={`plan-type-${opt.value}`}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Plants */}
              <div>
                <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Pflanzen *</label>
                <div className="max-h-36 overflow-y-auto space-y-1 p-2 rounded-lg" style={{ border: '1px solid #1B2A21', background: '#050A07' }}>
                  {plants.length === 0 ? (
                    <p className="text-xs text-emerald-100/30 text-center py-2">Keine Pflanzen</p>
                  ) : plants.map(p => (
                    <label key={p.id} className="flex items-center gap-2 p-1.5 rounded cursor-pointer hover:bg-[#111C15] transition-colors">
                      <input type="checkbox" checked={planForm.plant_ids.includes(p.id)} onChange={() => togglePlant(p.id)}
                        className="accent-emerald-500" data-testid={`plan-plant-${p.id}`} />
                      <span className="text-sm text-emerald-100">{p.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Amount + Notes */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Menge</label>
                  <input className="input-grow" placeholder="z.B. 500ml"
                    value={planForm.amount} onChange={e => setPlanForm(f => ({ ...f, amount: e.target.value }))}
                    data-testid="plan-amount-input" />
                </div>
                <div>
                  <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Notizen</label>
                  <input className="input-grow" placeholder="Optional"
                    value={planForm.notes} onChange={e => setPlanForm(f => ({ ...f, notes: e.target.value }))} />
                </div>
              </div>

              {planError && <p className="text-red-400 text-sm">{planError}</p>}
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setShowPlanModal(false)} className="btn-secondary flex-1">Abbrechen</button>
                <button type="submit" disabled={savingPlan} className="btn-primary flex-1 flex items-center justify-center gap-2" data-testid="save-plan-btn">
                  <Clock size={14} /> {savingPlan ? 'Planen...' : 'Einplanen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
